import { useGeld } from '../lib/geld';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { ShiftWithCalc } from '../types';
import { cn, formatDate, formatHours, formatMoney, formatTime, rateTypeBadgeClass, rateTypeLabel } from '../lib/utils';
import { shiftKind, KIND_LABEL, KIND_STYLE } from '../lib/shift-kind';

interface Props {
  date: string;
  shifts: ShiftWithCalc[];
  children: ReactNode;
  /** Waar het venster opent: onder de cel (standaard) of erboven (onderste weken). */
  placement?: 'bottom' | 'top';
  /** Uitlijning: links (eerste kolommen) of rechts (laatste kolommen), zodat het niet buiten beeld valt. */
  align?: 'left' | 'right';
}

/** Hover-venster bij een dagcel (desktop): per dienst tijd, object, uren, tarieven en bruto. */
export function DayPreviewTooltip({ date, shifts, children, placement = 'bottom', align = 'left' }: Props) {
  const [visible, setVisible] = useState(false);
  const total = shifts.reduce((sum, s) => sum + (s.total_gross ?? 0) + (s.reiskosten ?? 0), 0);

  return (
    <div
      className="relative min-w-0"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}

      {visible && shifts.length > 0 && (
        <div
          className={cn(
            'pointer-events-none absolute z-40 w-72 rounded-xl border border-surface-line bg-surface-raised p-3 animate-fade-in',
            placement === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1',
            align === 'left' ? 'left-0' : 'right-0'
          )}
          role="tooltip"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="t-num text-sm font-medium text-slate-100">{formatDate(date)}</span>
            <span className="t-label">
              {shifts.length} {shifts.length === 1 ? 'dienst' : 'diensten'}
            </span>
          </div>

          <div className="list">
            {shifts.map((shift) => (
              <ShiftPreviewBlock key={shift.id} shift={shift} />
            ))}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-surface-line pt-2 text-sm">
            <span className="text-slate-400">Totaal incl. reiskosten</span>
            <span className="t-num font-semibold text-money">{formatMoney(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ShiftPreviewBlock({ shift }: { shift: ShiftWithCalc }) {
  const { geld, label } = useGeld();
  const kind = shiftKind(shift);
  const object = shift.object?.naam ?? shift.object_naam_snapshot;
  return (
    <div className="space-y-1 py-2 first:pt-0">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className={cn('h-1.5 w-1.5 rounded-full', KIND_STYLE[kind].dot)} aria-hidden />
          <span className="t-num text-sm font-semibold text-slate-100">
            {formatTime(shift.start_time)}–{formatTime(shift.end_time)}
          </span>
          <span className="text-xs text-slate-400">{KIND_LABEL[kind]}</span>
        </span>
        <span className="t-num text-xs text-slate-400">{formatHours(shift.total_hours ?? 0)}</span>
      </div>
      {object && <div className="truncate text-xs text-slate-400">{object}</div>}
      {shift.segments?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {shift.segments.map((seg) => (
            <span key={seg.id} className={rateTypeBadgeClass(seg.rate_type)}>
              {rateTypeLabel(seg.rate_type)}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>{label}{shift.reiskosten > 0 ? ` + ${formatMoney(shift.reiskosten)} reiskosten` : ''}</span>
        <span className="t-num text-slate-200">{geld(shift.total_gross ?? 0, shift.shift_date)}</span>
      </div>
    </div>
  );
}
